import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useOnboardingFooter } from "./OnboardingShell";

/**
 * Bottom action row rendered at the foot of every form step.
 * Lives inside the content column (not sticky, no fixed chrome) —
 * the page reads top-to-bottom like a Notion document. Welcome and
 * Done steps render their own inline CTAs and skip this row
 * entirely.
 *
 * Reads handlers + UI hints from `useOnboardingFooter()` so each
 * step can drop the component in without re-wiring navigation.
 */
export function StepFooter() {
  const f = useOnboardingFooter();
  return (
    <FooterRow
      onBack={f.onBack}
      onSkip={f.onSkip}
      onContinue={f.onContinue}
      showBack={f.showBack}
      showSkip={f.showSkip}
      pending={f.pending}
      disabled={f.disabled}
      continueLabel={f.continueLabel}
    />
  );
}

function FooterRow({
  onBack,
  onSkip,
  onContinue,
  showBack,
  showSkip,
  pending,
  disabled,
  continueLabel,
}: {
  onBack: () => void;
  onSkip: () => void;
  onContinue: () => void;
  showBack: boolean;
  showSkip: boolean;
  pending: boolean;
  disabled: boolean;
  continueLabel: string;
}) {
  return (
    <div className="pt-6 border-t border-border flex items-center justify-between gap-3">
      <div>
        {showBack && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            disabled={pending}
          >
            ← Back
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {showSkip && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onSkip}
            disabled={pending}
            className="text-muted-foreground"
          >
            Skip for now
          </Button>
        )}
        <Button
          size="sm"
          onClick={onContinue}
          disabled={disabled || pending}
        >
          {pending ? "Saving…" : (
            <>
              {continueLabel}
              <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
